import type { Absence, ResourceUser, TimelineSlot } from '../types';
import { getUserWorktimeForDate } from './resources';

export type SlotTimeRange = {
  startTime: string | null;
  endTime: string | null;
};

/**
 * The absence a slot stands for, resolved the way the event renderer resolves
 * it (`getSelectedAbsenceDuration`). The slot does not always name it as the
 * edited one -- a lone selected id names it just as well -- and the two have to
 * agree, or hours seeded against an absence the renderer recognises come back
 * as a reshaped event.
 */
function slotAbsenceId(slot: TimelineSlot): number | null {
  return (
    slot.editedAbsenceId ??
    slot.editedAbsenceIds?.[0] ??
    (slot.selectedIds.length === 1 ? slot.selectedIds[0] : null)
  );
}

/**
 * The hours a slot opens on in day view.
 *
 * Whatever the user has already chosen wins. Failing that the hours are taken
 * from the absence in hand, then from an hourly absence already on the day, and
 * only then from the working schedule -- the last of which is for booking new
 * hours, not for reshaping something already booked.
 *
 * An all-day absence stops the chain: it has no hours to show, and inventing
 * some from the schedule redraws it as a block of work hours, which is not what
 * is booked. It stays whole until the user picks the hours to cut it down to,
 * and the form will not submit until they do.
 */
export function getSlotTimeRange(
  slot: TimelineSlot,
  absences: Absence[],
  users: ResourceUser[],
): SlotTimeRange {
  if (
    !slot.userId ||
    !slot.start ||
    !slot.end ||
    slot.startTime ||
    slot.endTime
  ) {
    return { startTime: slot.startTime, endTime: slot.endTime };
  }

  const absenceId = slotAbsenceId(slot);
  const absence =
    absenceId != null ? absences.find((a) => a.id === absenceId) : undefined;

  if (absenceId != null && !absence) {
    // The slot stands for an absence that is not in hand yet: opening the day
    // view refetches the range, and for the width of that fetch the absence it
    // was opened on is missing. Falling through to the schedule here is what
    // put work hours on an all-day absence -- and they stuck, because hours
    // once set are never overridden. Nothing is seeded until the absence
    // arrives and can answer for itself.
    return { startTime: null, endTime: null };
  }

  if (absence?.allDay) {
    return { startTime: null, endTime: null };
  }

  if (absence && absence.startTime != null && absence.endTime != null) {
    return { startTime: absence.startTime, endTime: absence.endTime };
  }

  const firstHourly = slot.selectedIds
    .map((id) => absences.find((a) => a.id === id))
    .filter(
      (a): a is Absence =>
        !!a && !a.allDay && a.startTime != null && a.endTime != null,
    )
    .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''))[0];

  if (firstHourly) {
    return { startTime: firstHourly.startTime, endTime: firstHourly.endTime };
  }

  const worktime = getUserWorktimeForDate(slot.userId, users, slot.start);

  if (worktime) {
    return { startTime: worktime.start, endTime: worktime.end };
  }

  return { startTime: null, endTime: null };
}
